import React, { useRef, useState } from "react";
import { Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export function ConfirmDeleteDialog({ open, title, description, confirmLabel = "Delete", busy, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="confirm-delete-dialog">
        <DialogHeader>
          <div className="w-11 h-11 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mb-2"><Trash2 size={20} /></div>
          <DialogTitle data-testid="confirm-delete-title">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <button type="button" data-testid="confirm-delete-cancel" disabled={busy} onClick={onCancel} className="px-4 py-2.5 rounded-full border border-neutral-200 text-sm font-semibold hover:bg-neutral-50 transition-colors">Cancel</button>
          <button type="button" data-testid="confirm-delete-confirm" disabled={busy} onClick={onConfirm} className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} {confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const REVEAL = 96;

export function SwipeRow({ children, onDelete, disabled, testId }) {
  const [x, setX] = useState(0);
  const drag = useRef(null);

  function down(e) {
    if (disabled || e.button > 0 || e.target.closest("button, a, input, select, textarea, label")) return;
    drag.current = { startX: e.clientX, startY: e.clientY, base: x, moved: false, axis: null };
  }
  function move(e) {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.axis && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) d.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
    if (d.axis !== "x") return;
    d.moved = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setX(Math.max(-REVEAL - 24, Math.min(0, d.base + dx)));
  }
  function up() {
    const d = drag.current;
    drag.current = null;
    if (!d || !d.moved) return;
    setX((cur) => (cur < -REVEAL / 2 ? -REVEAL : 0));
  }

  return (
    <div className="relative" style={{ overflowX: "clip", overflowY: "visible" }} data-testid={testId}>
      <button
        type="button"
        tabIndex={x ? 0 : -1}
        data-testid={`${testId}-swipe-delete`}
        onClick={() => { setX(0); onDelete(); }}
        className="absolute inset-y-0 right-0 flex items-center justify-center gap-1.5 bg-red-600 text-white text-sm font-semibold"
        style={{ width: REVEAL, visibility: x < 0 ? "visible" : "hidden" }}
        aria-label="Delete"
      >
        <Trash2 size={15} /> Delete
      </button>
      <div
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        className="relative bg-white select-none touch-pan-y"
        style={{ transform: `translateX(${x}px)`, transition: drag.current?.moved ? "none" : "transform 200ms ease-out" }}
      >
        {children}
      </div>
    </div>
  );
}
